import { LoaderFunctionArgs } from "react-router";
import { useLoaderData } from "react-router";
import { prisma as db } from "~/db/prisma.server";
import { getAuthFromRequest } from "~/services/auth.server";
import PlayHeader from "./PlayHeader";
import Container from "~/components/shared/Container";
import SessionList from "./SessionList";
import PlayDetails from "./PlayDetails";
import Play404 from "./404";
import type { Play, Session, Venue, PlayImage, PlayMedia, Rating } from "@prisma/client";
import { PLAY_STATUS } from "~/services/const";

type LoaderData = {
  play:
    | (Play & {
        sessions: (Session & { venue: Venue })[];
        images: PlayImage[];
        media: PlayMedia[];
        ratings?: Rating[];
      })
    | null;
  isAuthenticated: boolean;
};

export async function loader({ params, request }: LoaderFunctionArgs) {
  const authSession = await getAuthFromRequest(request);
  const identifier = params.slug_id!.split("-").pop();

  const query = {
    where: {
      slug_id: identifier,
      status: PLAY_STATUS.OK,
    },
    include: {
      sessions: {
        orderBy: {
          datetime: "asc",
        },
        include: { venue: true },
      },
      images: true,
      media: true,
    },
  };

  if (!!authSession) {
    query.include.ratings = {
      where: {
        accountId: authSession.id,
      },
    };
  }

  const play = await db.play.findUnique(query).then((p) => p);

  if (!play) {
    throw new Response("Play not found", { status: 404 });
  }

  return { play, isAuthenticated: !!authSession };
}

export function meta({ data }: { data: LoaderData | undefined }) {
  if (!data?.play) {
    return [
      { title: "Teatro.app — Peça não encontrada" },
      { description: "Esta peça não foi encontrada no Teatro.app" },
    ];
  }

  const play = data.play;
  const title = `Teatro.app — ${play.title}`;
  const description = play.synopsis
    ? play.synopsis.substring(0, 160)
    : "Sabe mais sobre a peça, quando são as sessões e onde comprar bilhetes.";
  const imageUrl = play.images?.[0]?.publicUrl;
  const playUrl = `https://teatro.app/play/${play.slug_id}`;
  const author = play.director || play.company || play.playwriter || "";
  return [
    { title },
    { description },
    { name: "description", content: description },

    // OpenGraph tags
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: "Teatro.app" },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: playUrl },
    ...(imageUrl ? [{ property: "og:image", content: imageUrl }] : []),

    // Twitter Card tags
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    ...(imageUrl ? [{ name: "twitter:image", content: imageUrl }] : []),

    // Additional metadata
    { name: "author", content: author },
    {
      name: "keywords",
      content: `teatro, ${play.genre}, ${play.title}, ${play.company || ""}, ${play.director || ""}`.trim(),
    },
  ];
}

export default function PlayDetailsRouter() {
  const { play, isAuthenticated } = useLoaderData<typeof loader>();

  if (!play) return <Play404 />;

  return (
    <div>
      <PlayHeader
        playId={play.id}
        isAuthenticated={isAuthenticated}
        title={play.title}
        coverUrl={play.images?.[0].publicUrl}
        attribution={play.director || play.company || play.playwriter}
        userRating={play?.ratings?.[0]?.score}
        averageRating={play.averageRating}
        totalRatings={play.totalRatings}
      />
      <Container>
        <div className="flex flex-col md:flex-row gap-8 relative -top-20 items-start">
          <section className="bg-white rounded shadow-sm w-full">
            <PlayDetails
              synopsis={play.synopsis}
              ageRating={play.ageRating}
              duration={play.duration}
              releaseDate={play.releaseDate}
              genre={play.genre}
              company={play.company}
              director={play.director}
              playwriter={play.playwriter}
              tech_sheet={play.tech_sheet}
              media={play.media}
            />
          </section>

          <section className="bg-white flex-1 w-full md:min-w-[320px] lg:min-w-[392px] rounded shadow-sm">
            <SessionList sessions={play.sessions} />
          </section>
        </div>
      </Container>
    </div>
  );
}
